import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

const securityChauffeur: ChauffeurPage = {
  slug: "security-chauffeur",
  title: "Security chauffeur & armored vehicle",
  subtitle:
    "Secure transport with trained chauffeur and armored vehicle across the French Riviera.",
  keywords: [
    "security chauffeur",
    "armored car with driver",
    "armored vehicle Cannes",
    "secure transport French Riviera",
  ],
  hero: {
    title: "Security chauffeur & armored vehicle on the French Riviera",
    subtitle:
      "Audi A8 L Security, Mercedes S680 Guard — total discretion, experienced chauffeurs, serene mobility for demanding clients.",
    keywords: ["armored vehicle", "security chauffeur", "VIP discretion"],
    imageSrc: heroImage.src,
    imageAlt: "Armored vehicle with security chauffeur on the French Riviera",
    badges: ["Armored vehicles", "Experienced chauffeurs", "Total discretion"],
  },
  benefits: {
    heading: "Why armored transport with KS Rent Services",
    items: [
      {
        title: "Armored vehicles",
        description:
          "Armored prestige sedans — Audi A8 L Security and Mercedes-Benz S680 Guard — combining protection and high-end comfort.",
      },
      {
        title: "Experienced chauffeurs",
        description:
          "Defensive driving, anticipation and composure: our chauffeurs are seasoned with sensitive travel.",
      },
      {
        title: "Absolute discretion",
        description:
          "Neutral attire, understated vehicles, strict confidentiality over your routes and meetings.",
      },
      {
        title: "Tailored coordination",
        description:
          "Route reconnaissance, confidential timing, private pick-up points and full flexibility.",
      },
    ],
  },
  services: {
    heading: "Secure transport services",
    services: [
      {
        title: "Secure transfers",
        text: "Nice airport, Monaco heliport, hotels and private residences, in full serenity.",
      },
      {
        title: "Personality mobility",
        text: "Business travel, executives, high-net-worth families and public figures.",
      },
      {
        title: "Sensitive events",
        text: "Festivals, summits, private parties: controlled access and discreet logistics.",
      },
      {
        title: "Hourly hire",
        text: "Chauffeur and armored vehicle by the hour or day, with a single point of contact.",
      },
    ],
  },
  locations: {
    heading: "Areas served",
    items: [
      { name: "Cannes" },
      { name: "Monaco" },
      { name: "Nice & airport" },
      { name: "Saint-Tropez" },
      { name: "Antibes — Cap d'Antibes" },
    ],
  },
  generic: {
    heading: "Protected mobility, no compromise on comfort",
    subheading:
      "Protection serving a premium experience, not the other way around.",
    paragraphs: [
      "Our armored vehicles keep the cabin, silence and refinement of a prestige sedan while offering a level of protection suited to sensitive trips.",
      "Every assignment is prepared in advance: routes, time slots and pick-up points are defined with you and kept confidential.",
      "You benefit from a single point of contact, real-time tracking and immediate adjustment capacity depending on context.",
    ],
    bullets: [
      "Routes scouted in advance",
      "Contractual confidentiality",
      "Available 24/7",
    ],
  },
  process: {
    heading: "How a secure assignment unfolds",
    steps: [
      "Confidential discussion of your need, constraints and schedule.",
      "Preparation of the route and pick-up points.",
      "Assignment of the armored vehicle and the right chauffeur.",
      "Discreet execution with real-time tracking and on-the-ground adjustments.",
    ],
  },
  faq: [
    {
      q: "Which armored vehicles do you offer?",
      a: "Armored prestige sedans: Audi A8 L Security and Mercedes-Benz S680 Guard, keeping the comfort of a limousine while offering reinforced protection.",
    },
    {
      q: "Are your chauffeurs trained for sensitive trips?",
      a: "Yes. Our chauffeurs practice anticipative, discreet driving and are used to demanding clients and sensitive contexts.",
    },
    {
      q: "Is confidentiality guaranteed?",
      a: "Absolutely: neutral attire, understated vehicles and strict confidentiality over your routes, schedules and meetings.",
    },
    {
      q: "Do you cover the major Riviera events?",
      a: "Yes, we operate at festivals, summits and private parties with controlled access logistics.",
    },
    {
      q: "How do I get a price?",
      a: "Secure transport is quoted individually based on route, duration and service level. Contact us for a fast, confidential proposal.",
    },
  ],
  relatedLinks: [
    { label: "Chauffeur hire Cannes", href: "/en/chauffeur/cannes" },
    { label: "Corporate chauffeur", href: "/en/chauffeur/business" },
    { label: "Our chauffeur transfers", href: "/en/chauffeur/transfers" },
  ],
  cta: {
    title: "A sensitive trip to organise?",
    text: "Let's talk in full confidentiality — fast quote for your armored transport.",
    primaryHref: "/contact",
    primaryLabel: "Request a confidential quote",
  },
  seo: {
    title: "Security chauffeur & armored vehicle — French Riviera",
    description:
      "Secure transport with chauffeur and armored vehicle (Audi A8 L Security, Mercedes S680 Guard) in Cannes, Monaco, Nice. Total discretion, confidential quote.",
    canonical: "/en/chauffeur/security-chauffeur",
  },
};

export default securityChauffeur;
